// Alternates (AlternatesViews on the Mac, spec §6.4): another version of a scene, a folder or the whole arrangement,
// written beside the original; nothing is ever deleted. New Alternate… forks the selected scene (an editable copy that
// opens in Write with a banner) or folder (a copy of it and everything in it in its own arrangement, "<folder> —
// <version>"); Compare Side by Side shows the core's `textDiff` in two columns (removed text struck through in red,
// added green, moved orange); Promote makes a version the manuscript's, the one it replaces becoming an alternate.
import { useMemo, useState } from 'react';
import { alternateSets, textDiff, type OpenedProject, type Segment } from '@writing-suite/core';
import { alternateText, currentMember, editTarget } from '@writing-suite/core/plan';
import type { MenuContext } from '../../rooms/menus.ts';
import { announce } from '../../shell/context.ts';
import type { PageProps } from '../../shell/ProjectShell.tsx';
import { formatRoute, navigate } from '../../shell/route.ts';
import { useWorkspace } from '../../shell/workspace.ts';
import { nodeOfFragment } from '../structure.ts';
import { planEdit } from '../../store/plan.ts';
import type { LiveProject } from '../../store/project.ts';
import { alternateSetAt, editingAlternate, nextVersionName } from '../../store/planModel.ts';
import { hasChildren } from '../helpers.ts';
import { TextDialog } from '../Margin.tsx';
import { DialogShell } from './BeatBoard.tsx';

type AlternateSet = ReturnType<typeof alternateSets>[number];
type Member = AlternateSet['members'][number];

interface Nav {
  projectId: string;
  hrefFor: PageProps['hrefFor'];
  navigate(href: string): void;
}

/** Where editing a member goes: a scene's copy in Write (`?alternate=`), or the arrangement holding a folder's version. */
export function editHref(n: Nav, set: AlternateSet, member: Member, project: OpenedProject): string | null {
  const target = editTarget(set, member);
  if (!target) return null;
  if ('fragmentID' in target) {
    const nodeId = set.nodeID ?? null;
    return n.hrefFor('write', nodeId, target.fragmentID ? { alternate: target.fragmentID } : {});
  }
  const manuscript = project.manuscriptId();
  return formatRoute({ name: 'project', projectId: n.projectId, room: 'write', page: 'write', nodeId: null,
    arrangementId: target.arrangementID === manuscript ? null : target.arrangementID, params: {} });
}

/** New Alternate…: forks the scene or folder at a node, then opens the new version. */
export function forkNode(live: LiveProject, n: Nav, project: OpenedProject, arrangementId: string, nodeId: string, folder: boolean, name: string) {
  const set = planEdit(live, (e) => (folder ? e.forkFolder(nodeId, arrangementId, name) : e.forkFragment(nodeId, arrangementId, name)));
  const made = set.members[set.members.length - 1];
  announce(`New alternate “${name}”`);
  const href = editHref(n, set, made, project);
  if (href) n.navigate(href);
}

/** Promote: the member becomes the current version. */
export function promoteMember(live: LiveProject, set: AlternateSet, member: Member) {
  planEdit(live, (e) => e.promote(member.id, set.id));
  announce(`“${member.name}” is now the current version`);
}

/** The text of each side, for Compare. */
function textOf(project: OpenedProject, live: LiveProject | null, set: AlternateSet, member: Member): string {
  const docs = live?.documents() ?? new Map();
  try {
    return alternateText(docs, project.record.arrangements, set, member);
  } catch {
    return '';
  }
}

/** Compare Side by Side: the current version on the left, the other on the right. */
export function CompareDialog({ project, live, set, member, onPromote, onClose }: {
  project: OpenedProject;
  live: LiveProject | null;
  set: AlternateSet;
  member: Member;
  onPromote?: () => void;
  onClose: () => void;
}) {
  const current = currentMember(set);
  const diff = useMemo(() => textDiff(current ? textOf(project, live, set, current) : '', textOf(project, live, set, member)), [project, live, set, member, current]);
  const side = (keep: (s: Segment) => boolean) =>
    diff.segments.filter(keep).map((s, i) => {
      switch (s.type) {
        case 'equal':
          return <span key={i}>{s.text}</span>;
        case 'deleted':
          return <del key={i}>{s.text}</del>;
        case 'inserted':
          return <ins key={i}>{s.text}</ins>;
        default:
          return (
            <span key={i} className="diff-moved" title={s.type === 'movedHere' ? 'Moved here' : 'Moved from here'}>
              {s.text}
            </span>
          );
      }
    });
  return (
    <DialogShell title={`Compare “${current?.name ?? 'Current'}” and “${member.name}”`} onCancel={onClose} className="compare-sheet">
      <p className="page-sub">
        {diff.wordsAdded} words added · {diff.wordsDeleted} cut · {diff.sentencesMoved} sentences moved
      </p>
      <div className="compare-columns">
        <section aria-label={current?.name ?? 'Current'} data-compare="current">
          <h3 className="eyebrow">{current?.name ?? 'Current'} (current)</h3>
          <div className="compare-text">{side((s) => s.type !== 'inserted' && s.type !== 'movedHere')}</div>
        </section>
        <section aria-label={member.name} data-compare="alternate">
          <h3 className="eyebrow">{member.name}</h3>
          <div className="compare-text">{side((s) => s.type !== 'deleted' && s.type !== 'movedAway')}</div>
        </section>
      </div>
      <div className="actions">
        {onPromote && member.id !== set.currentMemberID ? (
          <button type="button" className="button secondary" onClick={onPromote}>
            Promote “{member.name}”
          </button>
        ) : null}
        <button type="button" className="button primary" onClick={onClose}>
          Done
        </button>
      </div>
    </DialogShell>
  );
}

/** Plan ▸ Alternates… (the Project menu): the selected scene's or folder's versions, and a new one. */
export function AlternatesSheet(ctx: MenuContext & { onClose(): void }) {
  const project = ctx.project as OpenedProject;
  const node = ctx.node;
  const set = alternateSetAt(project, ctx.arrangement.id, node?.id);
  const [naming, setNaming] = useState(!set || set.members.length < 2);
  const [comparing, setComparing] = useState<Member | null>(null);
  const nav: Nav = { projectId: ctx.projectId, hrefFor: ctx.hrefFor, navigate: ctx.navigate };
  const writable = ctx.canStructure && !!ctx.live;
  if (!node) {
    return (
      <DialogShell title="Alternates" onCancel={ctx.onClose}>
        <p>Choose a scene or a folder on the shelf first.</p>
        <div className="actions">
          <button type="button" className="button primary" onClick={ctx.onClose}>
            Done
          </button>
        </div>
      </DialogShell>
    );
  }
  if (naming && writable) {
    return (
      <TextDialog
        title={`New Alternate of “${node.title || 'Untitled'}”`}
        label="Version"
        initial={nextVersionName(set)}
        confirm="Create"
        message={
          <p>
            {hasChildren(node)
              ? 'A copy of the folder and everything in it goes into its own arrangement, and opens. The original is untouched.'
              : 'A copy of the scene opens in the editor. The version in your manuscript is untouched until you promote this one.'}
          </p>
        }
        onCancel={() => (set && set.members.length > 1 ? setNaming(false) : ctx.onClose())}
        onDone={(name) => {
          ctx.onClose();
          forkNode(ctx.live!, nav, project, ctx.arrangement.id, node.id, hasChildren(node), name);
        }}
      />
    );
  }
  if (comparing && set) {
    return (
      <CompareDialog
        project={project}
        live={ctx.live}
        set={set}
        member={comparing}
        onPromote={writable ? () => { promoteMember(ctx.live!, set, comparing); setComparing(null); } : undefined}
        onClose={() => setComparing(null)}
      />
    );
  }
  return (
    <DialogShell title={`Alternates of “${node.title || 'Untitled'}”`} onCancel={ctx.onClose} className="alternates-sheet">
      <ul className="alternate-members">
        {(set?.members ?? []).map((m) => {
          const current = m.id === set!.currentMemberID;
          const href = editHref(nav, set!, m, project);
          return (
            <li key={m.id} data-member={m.name} data-current={current || undefined}>
              <span className="alternate-name">
                {m.name}
                {current ? <span className="alternate-current"> · current</span> : null}
              </span>
              <span className="alternate-actions">
                {href ? (
                  <button type="button" className="quiet-button" onClick={() => { ctx.onClose(); ctx.navigate(href); }}>
                    Edit
                  </button>
                ) : null}
                {!current ? (
                  <>
                    <button type="button" className="quiet-button" onClick={() => setComparing(m)}>
                      Compare Side by Side…
                    </button>
                    {writable ? (
                      <button type="button" className="quiet-button" onClick={() => promoteMember(ctx.live!, set!, m)}>
                        Promote
                      </button>
                    ) : null}
                  </>
                ) : null}
              </span>
            </li>
          );
        })}
      </ul>
      <div className="actions">
        {writable ? (
          <button type="button" className="button secondary" onClick={() => setNaming(true)}>
            New Alternate…
          </button>
        ) : null}
        <button type="button" className="button primary" onClick={ctx.onClose}>
          Done
        </button>
      </div>
    </DialogShell>
  );
}

/** Write's banner while an alternate scene is open (`?alternate=`): Back to Current, Compare, Promote. */
export function AlternateBanner({ project, arrangement, node, params, live, hrefFor, navigate }: Pick<PageProps, 'project' | 'arrangement' | 'node' | 'params' | 'live' | 'hrefFor'> & { navigate(href: string): void }) {
  const opened = project as OpenedProject;
  const editing = opened.record ? editingAlternate(opened, arrangement.id, node?.id, params.alternate) : null;
  const [comparing, setComparing] = useState(false);
  if (!editing || !node) return null;
  const back = hrefFor('write', node.id);
  const promote = () => {
    if (!live) return;
    promoteMember(live, editing.set, editing.member);
    navigate(back);
  };
  return (
    <div className="alternate-banner" role="status" data-alternate={editing.member.name}>
      <span>
        Writing “{editing.member.name}”, an alternate of “{editing.set.title || node.title}”
      </span>
      <span className="alternate-banner-actions">
        <button type="button" className="quiet-button" onClick={() => navigate(back)}>
          Back to Current
        </button>
        <button type="button" className="quiet-button" onClick={() => setComparing(true)}>
          Compare
        </button>
        {live ? (
          <button type="button" className="quiet-button" onClick={promote}>
            Promote
          </button>
        ) : null}
      </span>
      {comparing ? (
        <CompareDialog project={opened} live={live} set={editing.set} member={editing.member} onPromote={live ? promote : undefined} onClose={() => setComparing(false)} />
      ) : null}
    </div>
  );
}

/** The fragments Write shows while an alternate scene is open, else null (Write shows the node's as usual). */
export function alternateFragments(project: PageProps['project'], arrangementId: string, node: PageProps['node'], params: PageProps['params']): string[] | null {
  const opened = project as OpenedProject;
  if (!params.alternate || !opened.record) return null;
  const editing = editingAlternate(opened, arrangementId, node?.id, params.alternate);
  return editing ? [params.alternate] : null;
}


/** The inspector's Alternates section (the Mac's): the item's versions, New Alternate…, and Compare Side by Side… —
 *  for the page's item, or the scene the caret is in on Write. */
export function InspectorAlternates({ fragmentId }: { fragmentId: string }) {
  const ws = useWorkspace();
  const [sheet, setSheet] = useState(false);
  if (!ws) return null;
  const project = ws.project as OpenedProject;
  if (!project.record || (ws.arrangement.role !== 'manuscript' && ws.arrangement.role !== 'alternate')) return null;
  const node = ws.node?.fragmentId === fragmentId ? ws.node : nodeOfFragment(ws.tree, fragmentId);
  if (!node) return null;
  const set = alternateSetAt(project, ws.arrangement.id, node.id);
  const projectId = decodeURIComponent(location.pathname.split('/')[2] ?? '');
  const manuscript = project.manuscriptId();
  const hrefFor: PageProps['hrefFor'] = (ref, nodeId, params) => {
    const [room, page] = (ref.includes('/') ? ref : `${ref}/${ref}`).split('/');
    return formatRoute({ name: 'project', projectId, room: room as never, page, nodeId, arrangementId: ws.arrangement.id === manuscript ? null : ws.arrangement.id, params: params ?? {} });
  };
  const ctx = {
    projectId, project, tree: ws.tree, node, arrangement: ws.arrangement, live: ws.live, hrefFor, params: {}, page: undefined as never,
    canWrite: ws.canWrite, canStructure: ws.canStructure, navigate, openRoomSheet: () => {}, onClose: () => setSheet(false),
  } as MenuContext & { onClose(): void };
  return (
    <section className="inspector-alternates" aria-labelledby="inspector-alternates-title">
      <h3 id="inspector-alternates-title" className="eyebrow">
        Alternates
      </h3>
      <p className="page-sub">
        {set && set.members.length > 1 ? `${set.members.length} versions · current: ${currentMember(set)?.name ?? ''}` : 'No other versions yet.'}
      </p>
      <button type="button" className="quiet-button" disabled={!ws.canStructure} onClick={() => setSheet(true)}>
        {set && set.members.length > 1 ? 'Compare Side by Side…' : 'New Alternate…'}
      </button>
      {sheet ? <AlternatesSheet {...ctx} /> : null}
    </section>
  );
}
